// TODO: This file is temporary, and should be split into several smaller servicies.
import Service from '@ember/service';
import { service } from '@ember/service';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import Consts from '../utils/constants';
import Formulas from '../utils/formulas';
import Complex from '../utils/complex';
import PrettyNum from '../utils/pretty-num-wrapper';
import Utils from '../utils/utils';

import cloneDeep from 'lodash.clonedeep';
import upperFirst from 'lodash.upperfirst';

import { adcNoiseModel } from '@ebiz/aditools-adcdriving/src/models/adc-noise-model.js';

export default class DataService extends Service {
    @service toolData;
    @service selectionData;
    @service intl;
    @service circuitConfigurations;
    @service sigmaDeltaData;

    requiredAdcProps = null;
    requiredDriverProps = null;

    rfDisabled = false;
    rfRemoved = false;

    showOptionalSourceSettings = false;

    filterEnabled = true;

    showAdcVariants = {};
    showDriverVariants = {};

    /**
     * For now providing aliases to moved selected data
     * TODO: Possibly change all references and remove these
     */
    @alias('selectionData.selectedAdc') selectedAdc;
    @alias('selectionData.selectedDriver') selectedDriver;
    @alias('selectionData.selectedSourceType') selectedSourceType;
    @alias('selectionData.selectedTopology') selectedTopology;
    @alias('selectionData.selectedLsbSettling') selectedLsbSettling;
    @alias('selectionData.selectedMux') selectedMux;
    @alias('selectionData.gain') gain;

    showZoomFit = false; // Determines whether the zoom fit button should be shown.
    zoomFitScaling = true; // Whether the fixed scaling needs to occur, should occur on initial load

    @computed('selectedDriver.type') get vcmLabelText() {
        return this.selectedDriver.type === Consts.DRIVER_TYPE.AMP
            ? this.intl.t('input_labels.vcm')
            : this.intl.t('input_labels.vocm');
    }

    @computed('vref', 'vcm') get vp() {
        //return Math.min(this.vcm, (this.vref - this.vcm)); // this is wrong - at least, wrong for the AD7768
        return this.vref / 2; // fix for SAR-611
    }

    @computed('vp') get vout() {
        return this.vp * 2;
    }

    vSupplyPos = null;
    vSupplyNeg = null;

    /* inputSettlingChart based on the original showLsbChart
                                Value (old)  Value (new)  Check (old)                   Check (new)
        ADC Input (LSB Error)   true         1            model.showLsbChart            (eq model.showLsbChart 1)
        ADC Input (Volts)	    false        2            (is-not model.showLsbChart)   (is-not (eq model.showLsbChart 2))
        third (new) tab	        (n/a)        3
    */
    inputSettlingChart = null; // no default value; get from init or URL
    @computed('inputSettlingChart') get showLsbChart() {
        let inputSettlingChart = this.inputSettlingChart;
        return inputSettlingChart == 1;
    }

    init() {
        super.init();

        let toolData = this.toolData.data;

        this.set('colors', toolData.colors);
    }

    @alias('circuitConfigurations.multiplexed') multiplexed;

    @computed('selectionData.selectedAdc.internalMux') get disableMultiplex() {
        return this.selectionData.selectedAdc.internalMux;
    }

    @computed('sigmaDeltaData.isSigmaDeltaAdc', 'selectionData.selectedAdc.internalMux') get hideMultiplexed() {
        return this.sigmaDeltaData.isSigmaDeltaAdc || !!this.selectionData.selectedAdc.internalMux;
    }

    @computed('showAdcVariants.Seq') get isSequencerEnabled() {
        return this.showAdcVariants && this.showAdcVariants.Seq ? true : false;
    }

    @computed('showAdcVariants.Seq') get hasSequencerMode() {
        return this.showAdcVariants && 'Seq' in this.showAdcVariants ? true : false;
    }

    get seqInX() {
        return 'INx';
    }

    @computed('isSequencerEnabled') get seqInY() {
        return this.isSequencerEnabled ? 'INy' : 'INx';
    }

    setMultiplexedMetadata() {
        if (!this.sigmaDeltaData.isSigmaDeltaAdc && this.selectedAdc.internalMux) {
            this.set('multiplexed', true);
        } else {
            this.set('multiplexed', false);
        }
    }

    overrideFsample() {
        let fsample = this.sigmaDeltaData.getFsample();
        if (fsample || fsample === 0) {
            this.set('samplingRate', fsample);
        }
    }

    resetVrefSelect() {
        if (this.selectedAdc.vrefOptions) this.set('vrefSelect', { id: this.vref, description: this.vref });
    }

    overrideRCValues(resetOptions = {}) {
        let { resetRext, resetCext, resetSDRext, resetSDCext } = resetOptions;

        // Default to true if options not provided
        if (!(resetSDRext === false)) resetSDRext = true;
        if (!(resetSDCext === false)) resetSDCext = true;

        if (this.selectedAdc.disableFilter && this.filterEnabled) this.set('filterEnabled', false);
        else if (!this.selectedAdc.disableFilter && !this.filterEnabled) this.set('filterEnabled', true);

        if (!this.filterEnabled) {
            this.set('rext', 0);
            this.set('cext', 1e-15);
        } else {
            if (resetRext) this.set('rext', Math.max(this.selectedAdc.rext, this.selectedDriver.rfiltMin));
            if (resetCext) this.set('cext', this.selectedAdc.cext);

            let rcValues = this.sigmaDeltaData.getRCValues();
            if (rcValues) {
                if (resetSDRext) this.set('rext', rcValues.rext);
                if (resetSDCext) this.set('cext', rcValues.cext);
            }
        }
    }

    resetDriverForAdc() {
        let selectedAdc = this.selectedAdc,
            defaultDriver = selectedAdc.defaultDriver || this.selectionData.defaultDriver,
            foundDriver = this.driverOptions.filter((driver) => driver.part === defaultDriver);

        if (foundDriver && foundDriver.length == 1) {
            // We only want to keep track of the previous driver for the current adc
            this.set(`showDriverVariant`, false);
            this.set('selectionData.previousDriver', null);
            this.set('selectionData.selectedDriver', foundDriver[0]);
            this.switchVariant('driver');
        }
    }

    @computed('vref', 'selectedAdc.validateVcm') get vcmRange() {
        let minVcm = this.vref / 2,
            maxVcm = 2.5;

        if (this.selectedAdc.validateVcm) return { min: minVcm, max: maxVcm };
        else return null;
    }

    handleVcm(canSetAdjustable = true) {
        if (this.vcmRange && this.vcmRange.min === this.vcmRange.max) {
            this.set('isVcmFixed', true);
            this.set('vcm', this.vcmRange.min);
        } else if ('defaultVcm' in this.selectedAdc && !Utils.isNullOrWhiteSpace(this.selectedAdc.defaultVcm)) {
            this.set('isVcmFixed', false);
            if (canSetAdjustable) this.set('vcm', this.selectedAdc.defaultVcm);
        } else {
            this.set('isVcmFixed', true);
            this.set('vcm', this.vref / 2);
        }
    }

    setDefaultAdcData() {
        //Ember.beginPropertyChanges();

        this.resetDriverForAdc();

        this.set('samplingRate', this.selectedAdc.samplingRate);
        this.overrideFsample();

        this.set('vref', this.selectedAdc.vref);
        this.resetVrefSelect();
        this.handleVcm();

        this.set('cext', this.selectedAdc.cext);
        this.overrideRCValues({ resetRext: true });

        this.setDefaultTopology();

        this.set('selectedSourceType', this.selectionData.findSourceTypeFromAdcInputType(this.selectedAdc.type));

        this.set('zoomFitScaling', true);
        //endPropertyChanges();
    }

    setDefaultTopology() {
        this.set('selectedTopology', this.selectionData.defaultTopology);
    }

    //#region Selectable options

    @computed('selectionData.adcs', 'frequency', 'rext', 'vout') get adcOptions() {
        let adcs = this.selectionData.adcs,
            frequency = this.frequency,
            vout = this.vout;

        return adcs.map((adc) => {
            let option = cloneDeep(adc);
            option.thd = Formulas.calculateThd(frequency, option.datasheetRfilt, option);
            option.snr = Formulas.calculateSnr(
                option.type === Consts.ADC_TYPE.SE ? vout : vout * 2,
                option.adcNoisePtoP,
            );

            return option;
        });
    }

    @computed('adcOptions') get selectableAdcOptions() {
        return this.adcOptions.filter((a) => a.selectable);
    }

    @computed('selectionData.drivers') get driverOptions() {
        return this.selectionData.drivers;
    }

    @computed('driverOptions', 'selectedAdc.id') get selectableDriverOptions() {
        if (!this.selectedAdc || !this.driverOptions) return null;
        let recommendedDrivers = this.selectedAdc.recommendedDrivers;

        return this.driverOptions
            .filter((driver) => driver.selectable)
            .map((driver) => {
                let selectableDriver = cloneDeep(driver);
                selectableDriver.recommended = recommendedDrivers.includes(driver.part);
                selectableDriver.disabled =
                    this.selectedAdc.type === Consts.ADC_TYPE.SE && driver.type === Consts.DRIVER_TYPE.DIFF_AMP;
                return selectableDriver;
            });
    }

    @computed('selectedAdc.id') get groupSelectableDriverOptions() {
        let selectedAdc = this.selectedAdc;
        return selectedAdc.recommendedDrivers.length > 0;
    }

    //#endregion

    //#region variant

    updateShowVariant(type = 'adc') {
        let currentItem = this.get(`selectionData.selected${upperFirst(type)}`);
        let previousItem = this.get(`selectionData.previous${upperFirst(type)}`);

        if (currentItem.variants) {
            let showVariants = {};
            currentItem.variants.forEach((variant) => (showVariants[variant.code] = false));
            this.set(`show${upperFirst(type)}Variants`, showVariants);
            this.set(`show${upperFirst(type)}Variant`, false);
        } else if (currentItem && previousItem) {
            this.set(`show${upperFirst(type)}Variants`, {});

            const currentPart = currentItem.part.split('|')[0];
            const previousPart = previousItem.part.split('|')[0];
            if (currentPart !== previousPart) {
                this.set(`show${upperFirst(type)}Variant`, false);
            } else if (!(currentItem.variant && previousItem.variant)) {
                this.set(`show${upperFirst(type)}Variant`, false);
            } else if (!(currentItem.variant.code === previousItem.variant.code)) {
                this.set(`show${upperFirst(type)}Variant`, false);
            }
        } else {
            this.set(`show${upperFirst(type)}Variants`, {});
            this.set(`show${upperFirst(type)}Variant`, false);
        }
    }

    switchVariant(type = 'adc') {
        let item = this.get(`selected${upperFirst(type)}`), // ex: selectedAdc
            showVariant = this.get(`show${upperFirst(type)}Variant`), // ex: showAdcVariant
            showVariants = this.get(`show${upperFirst(type)}Variants`), // ex: showAdcVariants
            options = this.get(`${type}Options`), // ex: adcOptions
            currentPartCore = this.get(`selectionData.selected${upperFirst(type)}.partCore`), // ex: selectionData.selectedAdc.partCore
            currentPartVariant = this.get(`selectionData.selected${upperFirst(type)}.currentVariant`); // 'selectionData.selectedAdc.currentVariant');

        if (!item.hasVariant) return; // only show variant checkbox if there is one

        // Compute the new thing to show
        let switchCode;

        if (!!showVariants && Object.values(showVariants).length) {
            switchCode = Object.keys(showVariants)
                .filter((key) => showVariants[key])
                .join('_');
        } else if (showVariant) switchCode = item.variant.code;

        let newItem = switchCode ? `${item.partCore}|${switchCode}` : item.partCore;

        // find and update the selected item
        let foundItem = options.find((item) => item.part === newItem);
        if (foundItem && !(foundItem.part === item.part)) {
            // Use a clone to avoid polluting the original data.
            if (!(foundItem.partCore === currentPartCore && foundItem.currentVariant === currentPartVariant)) {
                this.set(`selectionData.selected${upperFirst(type)}`, foundItem);
            }
        }
    }

    //#endregion

    @computed('gain', 'rf', 'selectedTopology.id') get rg() {
        let result,
            selectedTopology = this.selectedTopology.id,
            gain = this.gain,
            rf = this.rf;

        if (selectedTopology === Consts.TOPOLOGY.NON_INVERTING || selectedTopology === Consts.TOPOLOGY.FOLLOWER) {
            if (gain === 1) result = Infinity;
            else result = rf / (gain - 1);
        } else result = rf / gain;

        return result;
    }

    @computed(
        'rg',
        'rf',
        'selectedTopology.id',
        'selectionData.{selectedAdc.partCore,selectedDriver.partCore}',
        'selectedSourceType',
    )
    get rload() {
        let result = 0,
            adcType = this.selectedAdc.type,
            driverType = this.selectedDriver.type,
            source = this.selectedSourceType,
            topology = this.selectedTopology.id,
            rf = this.rf,
            rg = this.rg;

        // if 2nd amp buffer on single-ended input source, use rload = rf (Diff ADC, SE source, SE Amp)
        if (
            adcType === Consts.ADC_TYPE.DIFF &&
            driverType === Consts.DRIVER_TYPE.AMP &&
            source.description === 'Single-ended'
        ) {
            result = rf;
        } else if (topology === Consts.TOPOLOGY.FOLLOWER) {
            result = Infinity;
        } else if (topology === Consts.TOPOLOGY.INVERTING) {
            result = rf;
        } else if (topology === Consts.TOPOLOGY.NON_INVERTING) {
            result = rf + rg;
        }
        return result;
    }

    @computed('rload', 'selectedDriver.Ro', 'vref') get roVoltageDrop() {
        let roVoltageDrop = 0,
            rload = this.rload,
            ro = this.selectedDriver.Ro,
            vref = this.vref;

        console.log('%crload = ' + rload + ', Ro = ' + ro, 'color: purple');

        if (rload === Infinity) {
            // do nothing, leave roVoltageDrop at 0
        } else {
            let loadCurrent = vref / 2 / rload; // assuming driver output is full scale (5V or 0) and rload is connected to Vcm
            roVoltageDrop = ro * loadCurrent;
            console.log('%cLoad Current = Vcm / rload = ' + loadCurrent, 'color: purple');
        }
        console.log('%croVoltageDrop = ' + roVoltageDrop, 'color: purple');
        return roVoltageDrop;
    }

    @computed('rg') get rgInf() {
        let rg = this.rg;
        return rg === Infinity;
    }

    @computed('vout', 'frequency') get inputSlewRate() {
        let vout = this.vout,
            frequency = this.frequency,
            voutvp = vout / 2,
            slew = 2 * Math.PI * frequency * voutvp;
        return slew;
    }

    // --------------------------------------------------
    // --------------------------------------------------

    //#region noise calculated properties

    // values that are populated in the noise table
    @computed(
        'selectedAdc',
        'selectedDriver',
        'selectedSourceType',
        'selectedTopology',
        'gain',
        'noiseGain',
        'signalRms',
        'vref',
        'rf',
        'rext',
        'cext',
        'cf',
        'noiseConfig',
        'wbNoise',
        'sigmaDeltaData.{odr,powerMode,filterType,channelAveraging,odrInput}',
    )
    get noiseCalculations() {
        //Second order noise calculations (cf and cin + cdiff effects)
        //GRfilt = 1/(1 + s * rext * cext)
        //Gmain = 1 / (rf * (cf + ci) * s^2 / (2 * pi * GBW) + ((rf + rg) / (2 * pi * GBW * rg) + rf * cf) * s + 1 )
        //Gib = rf * GRfilt * Gmain
        //GRG = -(rf / rg) * GRfilt * Gmain
        //GRF = GRfilt * Gmain
        //GAmp = ((rf + rg) / rg + rf * (cf + ci) * s ) * GRfilt * Gmain
        const frequencies = Array.from({ length: 1001 }, (_, i) => Math.pow(10, -1 + i * 0.01)); // Frequencies from 0.1 Hz to 1 GHz

        let driverBW = this.driverBandwidth,
            driverEquivBW = this.driverEqNoiseBW,
            adcEffectiveNoiseBW = this.adcEffectiveNoiseBW,
            driverEffectiveNoiseBW = this.driverEffectiveNoiseBW,
            cf = this.cf,
            ci = this.selectedDriver.Cin || 0,
            cdiff = this.selectedDriver.Cdiff || 0,
            gbw = this.selectedDriver.unityGain,
            rf = this.rf,
            rg = this.rg,
            rext = this.rext,
            cext = this.cext,
            effectiveNoiseBW = driverEffectiveNoiseBW;
        const hasCf = cf && cf > 0;
        let vnEffectiveBW, ibPlusEffectiveBW, ibMinusEffectiveBW, rfEffectiveBW, rgEffectiveBW;
        let sourceEquivBW = 1, //this.propertyName,
            sourceDensity = this.sourceNoiseDensity,
            sourceInputDensity = this.wbNoise,
            sourceNoiseGain = this.gain,
            sourceTotalVrms = Formulas.calculateNoiseRMS(sourceDensity, driverEffectiveNoiseBW),
            sourceTotalSnr = Formulas.calculateSnr(this.signalRms, sourceTotalVrms),
            //vnEquivBW = driverBW * (Math.PI / 2),
            vnDensity = this.ampVoltageNoiseDensity,
            vnSourceInputDensity = this.selectedDriver.vn,
            vnSourceNoiseGain = this.ampVoltageNoiseDensityMultiplier,
            vnNoiseDensityCurve = frequencies.map(
                (f) =>
                    Formulas.calculateAmplifierGain(f, rf, rg, cf, ci + cdiff, gbw, rext, cext).magnitude() *
                    vnSourceInputDensity,
            ),
            vnTotalVrms = hasCf
                ? vnSourceNoiseGain * Formulas.calculateNoiseRMSbyIntegration(frequencies, vnNoiseDensityCurve)
                : Formulas.calculateNoiseRMS(vnDensity, driverEffectiveNoiseBW),
            vnTotalPerc = '',
            vnTotalSnr = '',
            ibPlusDensity = this.ampIbPlusNoiseDensity,
            ibPlusSourceInputDensity = this.ampIbPlusNoiseDensityInput,
            ibPlusSourceNoiseGain = this.ampIbPlusNoiseMultiplier,
            ibPlusNoiseDensityCurve = frequencies.map(
                (f) =>
                    Formulas.calculateIbGain(f, rf, rg, cf, ci + cdiff, gbw, rext, cext).magnitude() *
                    this.selectedDriver.inp,
            ),
            ibPlusTotalVrms =
                hasCf && ibPlusDensity > 0
                    ? ibPlusSourceNoiseGain *
                      Formulas.calculateNoiseRMSbyIntegration(frequencies, ibPlusNoiseDensityCurve)
                    : Formulas.calculateNoiseRMS(ibPlusDensity, effectiveNoiseBW),
            ibPlusTotalPerc = '',
            ibPlusTotalSnr = '',
            //ibEquivBW = driverBW * (Math.PI / 2),
            ibMinusDensity = this.ampIbMinusNoiseDensity,
            ibMinusSourceInputDensity = this.ampIbMinusNoiseDensityInput,
            ibMinusSourceNoiseGain = this.ampIbMinusNoiseMultiplier,
            ibMinusNoiseDensityCurve = frequencies.map(
                (f) =>
                    Formulas.calculateIbGain(f, rf, rg, cf, ci + cdiff, gbw, rext, cext).magnitude() *
                    this.selectedDriver.inn,
            ),
            ibMinusTotalVrms = hasCf
                ? ibMinusSourceNoiseGain *
                  Formulas.calculateNoiseRMSbyIntegration(frequencies, ibMinusNoiseDensityCurve)
                : Formulas.calculateNoiseRMS(ibMinusDensity, effectiveNoiseBW),
            ibMinusTotalPerc = '',
            ibMinusTotalSnr = '',
            //rfEquivBW = 1,
            rfDensity = this.ampRfNoiseDensity,
            rfSourceInputDensity = this.ampRfNoiseDensityInput,
            rfSourceNoiseGain = this.ampRfNoiseMultFactor,
            rfSourceNoiseDensityCurve = frequencies.map(
                (f) => Formulas.calculateRfGain(f, rf, rg, cf, ci + cdiff, gbw, rext, cext).magnitude() * rfDensity,
            ),
            rfTotalVrms = hasCf
                ? Formulas.calculateNoiseRMSbyIntegration(frequencies, rfSourceNoiseDensityCurve)
                : Formulas.calculateNoiseRMS(rfDensity, effectiveNoiseBW),
            rfTotalPerc = '',
            rfTotalSnr = '',
            //rgEquivBW = 1,
            rgDensity = this.ampRgNoiseDensity,
            rgSourceInputDensity = this.ampRgNoiseDensityInput,
            rgSourceNoiseGain = this.ampRgNoiseMultFactor,
            rgSourceNoiseDensityCurve = frequencies.map(
                (f) => Formulas.calculateRgGain(f, rf, rg, cf, ci + cdiff, gbw, rext, cext).magnitude() * rgDensity,
            ),
            rgTotalVrms = hasCf
                ? Formulas.calculateNoiseRMSbyIntegration(frequencies, rgSourceNoiseDensityCurve)
                : Formulas.calculateNoiseRMS(rgDensity, effectiveNoiseBW),
            rgTotalPerc = '',
            rgTotalSnr = '',
            driverDensity = Formulas.calculateRSS([vnDensity, ibMinusDensity, ibPlusDensity, rfDensity, rgDensity]),
            driverTotalVrms = hasCf
                ? Formulas.calculateRSS([vnTotalVrms, ibMinusTotalVrms, ibPlusTotalVrms, rfTotalVrms, rgTotalVrms])
                : Formulas.calculateNoiseRMS(driverDensity, driverEffectiveNoiseBW),
            driverTotalSnr = Formulas.calculateSnr(this.signalRms, driverTotalVrms),
            //rfiltBW = this.sigmaDeltaData.isSigmaDeltaAdc ? sigmaDeltaAliasBW : this.filterBandwidth,
            rfiltBW = this.filterBandwidth,
            rfiltEquivBW = this.filterEqNoiseBW,
            rfiltEffectiveBW = this.filterEffectiveNoiseBW,
            rfiltDensity = this.ampRfiltNoiseDensity,
            rfiltTotalVrms = Formulas.calculateNoiseRMS(rfiltDensity, rfiltEffectiveBW),
            rfiltTotalSnr = '',
            adcBW = this.adcBandwidth,
            adcEquivBW = this.adcEquivNoiseBandwidth,
            adcDensity = this.adcNoiseDensity,
            adcTotalVrms = this.adcNoiseContribution,
            adcTotalSnr = Formulas.calculateSnr(this.signalRms, adcTotalVrms),
            totalEquivBW = '',
            totalDensity = '',
            totalVrms = Formulas.calculateRSS([sourceTotalVrms, driverTotalVrms, rfiltTotalVrms, adcTotalVrms]),
            totalPerc = '',
            totalSnr = Formulas.calculateSnr(this.signalRms, totalVrms),
            sourceTotalPerc = Formulas.calculateNoisePercent(sourceTotalVrms, totalVrms),
            driverTotalPerc = Formulas.calculateNoisePercent(driverTotalVrms, totalVrms),
            rfiltTotalPerc = Formulas.calculateNoisePercent(rfiltTotalVrms, totalVrms),
            adcTotalPerc = Formulas.calculateNoisePercent(adcTotalVrms, totalVrms);

        if (hasCf) {
            // Calculate equivalent noise BW from integrated results
            // BW_equiv = (Vrms / density)^2
            driverEffectiveNoiseBW = Math.pow(driverTotalVrms / driverDensity, 2);
            vnEffectiveBW = Math.pow(vnTotalVrms / vnDensity, 2);
            ibPlusEffectiveBW = ibPlusDensity > 0 ? Math.pow(ibPlusTotalVrms / ibPlusDensity, 2) : effectiveNoiseBW; // avoid NaN if density is 0
            ibMinusEffectiveBW = Math.pow(ibMinusTotalVrms / ibMinusDensity, 2);
            rfEffectiveBW = rfDensity > 0 ? Math.pow(rfTotalVrms / rfDensity, 2) : effectiveNoiseBW; // avoid NaN if density is 0
            rgEffectiveBW = rgDensity > 0 ? Math.pow(rgTotalVrms / rgDensity, 2) : effectiveNoiseBW; // avoid NaN if density is 0
            if (rfDensity === 0) rfTotalVrms = 0; // should not happen outside of unit tests
            if (rgDensity === 0) rgTotalVrms = 0; // should not happen outside of unit tests
            console.log(rfDensity, 'rfDensity', rgDensity, 'rgDensity');
        } else {
            // Use the standard pi/2 * bandwidth when cf = 0
            vnEffectiveBW = effectiveNoiseBW;
            ibPlusEffectiveBW = effectiveNoiseBW;
            ibMinusEffectiveBW = effectiveNoiseBW;
            rfEffectiveBW = effectiveNoiseBW;
            rgEffectiveBW = effectiveNoiseBW;
        }

        // console.log(`
        //     Noise Config: ${this.noiseConfig}
        //     `);
        // console.log(`
        //     Rf Density: ${(rfDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Rf Total Vrms: ${(rfTotalVrms * 1e6).toFixed(3)} µV,
        //     Rf Effective Noise BW: ${rfEffectiveBW},
        //     Rf Source Noise Gain: ${rfSourceNoiseGain}`);
        // console.log(`
        //     Rg Density: ${(rgDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Rg Total Vrms: ${(rgTotalVrms * 1e6).toFixed(3)} µV,
        //     Rg Effective Noise BW: ${rgEffectiveBW},
        //     Rg Source Noise Gain: ${rgSourceNoiseGain}`);
        // console.log(`
        //     Rext Density: ${(rfiltDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Rext Total Vrms: ${(rfiltTotalVrms * 1e6).toFixed(3)} µV,
        //     Rext Effective Noise BW: ${(rfiltTotalVrms / rfiltDensity / 1) ** 2},
        //     Rext Source Noise Gain: 1`);
        // console.log(`
        //     Vn Density: ${(vnDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Vn Total Vrms: ${(vnTotalVrms * 1e6).toFixed(3)} µV,
        //     Vn Effective Noise BW: ${vnEffectiveBW},
        //     Vn Source Noise Gain: ${vnSourceNoiseGain}`);
        // console.log(`
        //     Ib- Density: ${(ibMinusDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Ib- Total Vrms: ${(ibMinusTotalVrms * 1e6).toFixed(3)} µV,
        //     Ib- Effective Noise BW: ${ibMinusEffectiveBW},
        //     Ib- Source Noise Gain: ${ibMinusSourceNoiseGain}`);
        // console.log(`
        //     Ib+ Density: ${(ibPlusDensity * 1e9).toFixed(3)} nV/Hz½,
        //     Ib+ Total Vrms: ${(ibPlusTotalVrms * 1e6).toFixed(3)} µV,
        //     Ib+ Effective Noise BW: ${ibPlusEffectiveBW},
        //     Ib+ Source Noise Gain: ${ibPlusSourceNoiseGain}`);

        let result = {
            total: {
                equivBW: totalEquivBW,
                density: totalDensity,
                sourceInputDensity: '',
                sourceNoiseGain: '',
                totalVrms: totalVrms,
                totalPerc: totalPerc,
                totalSnr: totalSnr,
            },
            source: {
                equivBW: sourceEquivBW,
                effectiveBW: driverEffectiveNoiseBW,
                density: sourceDensity,
                sourceInputDensity: sourceInputDensity,
                sourceNoiseGain: sourceNoiseGain,
                totalVrms: sourceTotalVrms,
                totalPerc: sourceTotalPerc,
                totalSnr: sourceTotalSnr,
            },
            adc: {
                bw: adcBW,
                equivBW: adcEquivBW,
                effectiveBW: adcEffectiveNoiseBW,
                density: adcDensity,
                sourceInputDensity: '',
                sourceNoiseGain: '',
                totalVrms: adcTotalVrms,
                totalPerc: adcTotalPerc,
                totalSnr: adcTotalSnr,
            },
            driver: {
                bw: driverBW,
                equivBW: driverEquivBW,
                effectiveBW: driverEffectiveNoiseBW,
                density: driverDensity,
                sourceInputDensity: '',
                sourceNoiseGain: '',
                totalVrms: driverTotalVrms,
                totalPerc: driverTotalPerc,
                totalSnr: driverTotalSnr,
                vn: Formulas.getNoiseCalculationItem(
                    vnDensity,
                    vnSourceInputDensity,
                    vnSourceNoiseGain,
                    vnTotalVrms,
                    vnTotalPerc,
                    vnTotalSnr,
                    vnEffectiveBW,
                ),
                ibPlus: Formulas.getNoiseCalculationItem(
                    ibPlusDensity,
                    ibPlusSourceInputDensity,
                    ibPlusSourceNoiseGain,
                    ibPlusTotalVrms,
                    ibPlusTotalPerc,
                    ibPlusTotalSnr,
                    ibPlusEffectiveBW,
                ),
                ibMinus: Formulas.getNoiseCalculationItem(
                    ibMinusDensity,
                    ibMinusSourceInputDensity,
                    ibMinusSourceNoiseGain,
                    ibMinusTotalVrms,
                    ibMinusTotalPerc,
                    ibMinusTotalSnr,
                    ibMinusEffectiveBW,
                ),
                //rfilt: Formulas.getNoiseCalculationItem(rfiltDensity, rfiltTotalVrms, rfiltTotalPerc, rfiltTotalSnr, rfiltBW),
                rf: Formulas.getNoiseCalculationItem(
                    rfDensity,
                    rfSourceInputDensity,
                    rfSourceNoiseGain,
                    rfTotalVrms,
                    rfTotalPerc,
                    rfTotalSnr,
                    rfEffectiveBW,
                ),
                rg: Formulas.getNoiseCalculationItem(
                    rgDensity,
                    rgSourceInputDensity,
                    rgSourceNoiseGain,
                    rgTotalVrms,
                    rgTotalPerc,
                    rgTotalSnr,
                    rgEffectiveBW,
                ),
            },
            rfilt: {
                bw: rfiltBW,
                equivBW: rfiltEquivBW,
                effectiveBW: rfiltEffectiveBW,
                density: rfiltDensity,
                sourceInputDensity: '',
                sourceNoiseGain: '',
                totalVrms: rfiltTotalVrms,
                totalPerc: rfiltTotalPerc,
                totalSnr: rfiltTotalSnr,
            },
        };

        return result;
    }

    //** Calculates the small signal bandwidth for noise calculations
    //** uses UGBW, shifts by noise gain, to dervice BW at selected gain
    @computed('selectedTopology.id', 'gain') get noiseGain() {
        let topology = this.selectedTopology.id,
            gain = this.gain;

        return topology === Consts.TOPOLOGY.INVERTING ? gain + 1 : gain;
    }

    @computed('selectedAdc.type', 'vout') get signal() {
        let vout = this.vout,
            adcType = this.selectedAdc.type;
        return adcType === Consts.ADC_TYPE.SE ? vout : vout * 2;
    }

    @computed('selectedAdc.type', 'signal') get signalRms() {
        let signal = this.signal;
        return signal / (2 * Math.sqrt(2));
    }

    //#region bandwidth calculations

    @computed('selectedDriver.unityGain', 'noiseGain') get driverBandwidth() {
        let unityGain = this.selectedDriver.unityGain,
            noiseGain = this.noiseGain,
            ssbw = unityGain / noiseGain;
        return ssbw;
    }

    // @computed('selectedDriver.{unityGain,Cin,Cdiff}', 'noiseGain', 'cf') get driverBandwidth() {
    //     let unityGain = this.selectedDriver.unityGain,
    //         noiseGain = this.noiseGain,
    //         cf = this.cf,
    //         Cin = this.selectedDriver.Cin || 0,
    //         Cdiff = this.selectedDriver.Cdiff || 0;

    //     // Adjust noiseGain when cf is defined and non-zero
    //     if (cf && cf > 0) {
    //         noiseGain = 1 + (Cin + Cdiff) / cf;
    //     }
    //     let ssbw = unityGain / noiseGain;
    //     return ssbw;
    // }

    @computed('rext', 'cext', 'selectedAdc.cadc') get filterBandwidth() {
        let rext = this.rext,
            cext = ['AD4080', 'AD4081', 'AD4084'].includes(this.selectedAdc.partCore) ? 2 * this.cext : this.cext,
            cadc = this.selectedAdc.cadc;
        return 1 / (Math.PI * 2 * (cext + cadc) * rext);
    }

    @computed('sigmaDeltaData.isSigmaDeltaAdc', 'selectedAdc.{radc,cadc}') get adcBandwidth() {
        let isSigmaDeltaAdc = this.sigmaDeltaData.isSigmaDeltaAdc,
            radc = this.selectedAdc.radc,
            cadc = this.selectedAdc.cadc;

        if (isSigmaDeltaAdc) {
            return '';
        } else {
            // might want to round this and label as approx
            return 1 / (2 * Math.PI * radc * cadc);
        }
    }

    @computed('driverBandwidth') get driverEqNoiseBW() {
        return Formulas.calculateEqNoiseBW(this.driverBandwidth);
    }

    @computed('filterBandwidth') get filterEqNoiseBW() {
        return Formulas.calculateEqNoiseBW(this.filterBandwidth);
    }

    @computed('sigmaDeltaData.adcNoiseCalculationModel') get adcNoiseCalculationModel() {
        if (this.sigmaDeltaData.adcNoiseCalculationModel)
            return adcNoiseModel(this.sigmaDeltaData.adcNoiseCalculationModel);
        else return null;
    }

    @computed('adcNoiseCalculationModel.noiseBW', 'sigmaDeltaData.{adcEquivNoiseBW,isSigmaDeltaAdc}', 'adcBandwidth')
    get adcEquivNoiseBandwidth() {
        if (this.adcNoiseCalculationModel) return this.adcNoiseCalculationModel.noiseBW;
        else if (this.sigmaDeltaData.isSigmaDeltaAdc) return this.sigmaDeltaData.adcEquivNoiseBW;
        else return Formulas.calculateEqNoiseBW(this.adcBandwidth);
    }

    @computed('driverEqNoiseBW', 'filterEqNoiseBW', 'adcEquivNoiseBandwidth', 'sigmaDeltaData.{fmod,isSigmaDeltaAdc}')
    get SigmaDeltaAliasBW() {
        let driverEquivBW = this.driverEqNoiseBW,
            rfiltEquivBW = this.filterEqNoiseBW,
            adcEquivBW = this.adcEquivNoiseBandwidth,
            fmod = this.sigmaDeltaData.fmod,
            isSigmaDeltaAdc = this.sigmaDeltaData.isSigmaDeltaAdc,
            aliasBW;

        if (!isSigmaDeltaAdc || !Utils.isNumeric(fmod)) {
            aliasBW = adcEquivBW; //for AD7134, and not AD7768
        } else {
            aliasBW = Formulas.calculateDriverAndSourceEquivBw({
                driverEquivBW,
                rfiltEquivBW,
                adcEquivBW,
                fmod,
            });
        }

        return aliasBW;
    }

    @computed('SigmaDeltaAliasBW', 'adcEffectiveNoiseBW', 'sigmaDeltaData.isSigmaDeltaAdc')
    get driverEffectiveNoiseBW() {
        let sigmaDeltaAliasBW = this.SigmaDeltaAliasBW,
            isSigmaDeltaAdc = this.sigmaDeltaData.isSigmaDeltaAdc,
            adcEffectiveNoiseBW = this.adcEffectiveNoiseBW,
            driverEffectiveNoiseBW;

        if (isSigmaDeltaAdc) {
            driverEffectiveNoiseBW = sigmaDeltaAliasBW;
        } else {
            driverEffectiveNoiseBW = adcEffectiveNoiseBW;
        }

        return driverEffectiveNoiseBW;
    }

    @computed('sigmaDeltaData.isSigmaDeltaAdc', 'filterEqNoiseBW', 'adcEquivNoiseBandwidth', 'driverEffectiveNoiseBW')
    get filterEffectiveNoiseBW() {
        let isSigmaDeltaAdc = this.sigmaDeltaData.isSigmaDeltaAdc,
            filterEqNoiseBW = this.filterEqNoiseBW,
            adcEquivNoiseBandwidth = this.adcEquivNoiseBandwidth,
            driverEffectiveNoiseBW = this.driverEffectiveNoiseBW,
            filterEffectiveNoiseBW;

        if (isSigmaDeltaAdc) {
            filterEffectiveNoiseBW = driverEffectiveNoiseBW;
        } else {
            filterEffectiveNoiseBW = Math.min(filterEqNoiseBW, adcEquivNoiseBandwidth);
        }

        return filterEffectiveNoiseBW;
    }

    @computed('driverEqNoiseBW', 'filterEqNoiseBW', 'adcEquivNoiseBandwidth') get adcEffectiveNoiseBW() {
        return Math.min(this.filterEqNoiseBW, this.driverEqNoiseBW, this.adcEquivNoiseBandwidth);
    }

    //#endregion bandwidth calculations

    //#region noise density calculations

    @computed('wbNoise', 'gain', 'noiseConfig') get sourceNoiseDensity() {
        let wbNoise = this.wbNoise,
            gain = this.gain,
            noiseConfig = this.noiseConfig,
            sourceNoiseBase = wbNoise * gain;
        return noiseConfig == Consts.CONFIGURATION.DiffADC_SEAmp_SESource ? 2 * sourceNoiseBase : sourceNoiseBase;
    }

    @computed('noiseGain', 'noiseConfig') get ampVoltageNoiseDensityMultiplier() {
        let noiseGain = this.noiseGain,
            noiseConfig = this.noiseConfig,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
                multiplier = Math.sqrt(2) * noiseGain;
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                multiplier = Math.sqrt(4 * noiseGain * noiseGain + 4);
                break;
            default:
                // Consts.CONFIGURATION.DiffADC_DiffAmp_SESource Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource
                multiplier = noiseGain;
        }

        return multiplier;
    }

    @computed('selectedDriver.vn', 'ampVoltageNoiseDensityMultiplier') get ampVoltageNoiseDensity() {
        let vTotalNoiseBase = this.selectedDriver.vn,
            multiplier = this.ampVoltageNoiseDensityMultiplier;

        return vTotalNoiseBase * multiplier;
    }

    @computed('rg') get ampRgNoiseDensityInput() {
        var rg = this.rg,
            rgNoiseBase;

        if (rg === Infinity) {
            rgNoiseBase = 0;
        } else {
            rgNoiseBase = Math.sqrt(4 * this.kt * rg);
        }

        return rgNoiseBase;
    }

    @computed('noiseConfig', 'rf', 'rg') get ampRgNoiseMultFactor() {
        var noiseConfig = this.noiseConfig,
            rf = this.rf,
            rg = this.rg,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
                multiplier = Math.SQRT2;
                console.log('%cDiff ADC, DiffAmp - Rg noise is uncorrelated, multiplier is sqrt(2)', 'color:pink');
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
                multiplier = Math.SQRT2;
                console.log(
                    '%cDiff ADC, SEAmp, DiffSource - Rg noise is uncorrelated, multiplier is sqrt(2)',
                    'color:pink',
                );
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                multiplier = 2;
                console.log('%cDiff ADC, SEAmp, SESource - Rg noise is correlated, multiplier is 2', 'color:pink');
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                multiplier = 1;
        }

        return (rf / rg) * multiplier;
    }

    @computed('ampRgNoiseDensityInput', 'ampRgNoiseMultFactor') get ampRgNoiseDensity() {
        var rgNoiseBase = this.ampRgNoiseDensityInput,
            multiplier = this.ampRgNoiseMultFactor;

        return rgNoiseBase * multiplier;
    }

    @computed('rf', 'rg') get ampRfNoiseDensityInput() {
        let rf = this.rf,
            rg = this.rg,
            rfNoiseBase;

        if (rg === Infinity) {
            rfNoiseBase = 0;
        } else {
            rfNoiseBase = Math.sqrt(4 * this.kt * rf);
        }

        return rfNoiseBase;
    }

    @computed('noiseConfig', 'selectedTopology', 'gain') get ampRfNoiseMultFactor() {
        let noiseConfig = this.noiseConfig,
            topology = this.selectedTopology,
            gain = this.gain,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
                multiplier = Math.SQRT2;
                console.log('%cDiff ADC, DiffAmp - Rf noise is uncorrelated, multiplier is sqrt(2)', 'color:pink');
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
                multiplier = Math.SQRT2;
                console.log(
                    '%cDiff ADC, SEAmp, Diff Source - Rf noise is uncorrelated, multiplier is sqrt(2)',
                    'color:pink',
                );
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                if (topology.id === Consts.TOPOLOGY.FOLLOWER) {
                    multiplier = Math.SQRT2;
                    console.log(
                        '%cDiff ADC, SEAmp, SESource, Follower - Rf noise is uncorrelated, multiplier is sqrt(2)',
                        'color:pink',
                    );
                } else if (topology.id === Consts.TOPOLOGY.NON_INVERTING && gain == 1) {
                    multiplier = Math.SQRT2;
                    console.log(
                        '%cDiff ADC, SEAmp, SESource, Non-Inv, gain = 1 - Rf noise is uncorrelated, multiplier is sqrt(2)',
                        'color:pink',
                    );
                } else {
                    multiplier = Math.sqrt(6);
                    console.log(
                        '%cDiff ADC, SEAmp, SESource - Rf noise is complicated, base noise dens is , multiplier is sqrt(6)',
                        'color:pink',
                    );
                }
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                multiplier = 1;
        }

        return multiplier;
    }

    @computed('ampRfNoiseDensityInput', 'ampRfNoiseMultFactor') get ampRfNoiseDensity() {
        var rfNoiseBase = this.ampRfNoiseDensityInput,
            multiplier = this.ampRfNoiseMultFactor;

        return rfNoiseBase * multiplier;
    }

    @computed('rext') get ampRfiltNoiseDensityInput() {
        let rext = this.rext;

        return Math.sqrt(4 * this.kt * rext);
    }

    @computed('noiseConfig') get ampRfiltNoiseMultFactor() {
        let noiseConfig = this.noiseConfig,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                multiplier = Math.SQRT2;
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                multiplier = 1;
        }

        return multiplier;
    }

    @computed('rext', 'noiseConfig') get ampRfiltNoiseDensity() {
        let rext = this.rext,
            rfiltNoiseBase = Math.sqrt(4 * this.kt * rext),
            noiseConfig = this.noiseConfig,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                multiplier = Math.SQRT2;
                console.log(
                    '%cDiff ADC - Rfilt noise is uncorrelated, base noise dens is ' +
                        rfiltNoiseBase +
                        ', multiplier is sqrt(2)',
                    'color:pink',
                );
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                multiplier = 1;
        }

        return rfiltNoiseBase * multiplier;
    }

    @computed('selectedDriver.inn', 'rf', 'rg') get ampIbMinusNoiseDensityInput() {
        let inn = this.selectedDriver.inn,
            rf = this.rf,
            rg = this.rg,
            parallelRs;

        if (rg === Infinity) {
            parallelRs = 0;
        } else {
            parallelRs = Formulas.parallel(rf, rg);
        }

        return inn * parallelRs;
    }

    @computed('ampIbMinusNoiseDensityInput', 'noiseConfig') get ampIbPlusNoiseDensityInput() {
        let ibMinusInputDensity = this.ampIbMinusNoiseDensityInput,
            noiseConfig = this.noiseConfig,
            ibPlusNoiseBase;

        switch (
            noiseConfig // if using a diff amp, ib+ = ib-
        ) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
                ibPlusNoiseBase = ibMinusInputDensity;
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                ibPlusNoiseBase = 0;
        }

        return ibPlusNoiseBase;
    }

    @computed('noiseConfig', 'noiseGain') get ampIbMinusNoiseMultiplier() {
        let noiseConfig = this.noiseConfig,
            noiseGain = this.noiseGain,
            multiplier;

        switch (noiseConfig) {
            case Consts.CONFIGURATION.DiffADC_DiffAmp_DiffSource:
            case Consts.CONFIGURATION.DiffADC_DiffAmp_SESource:
                multiplier = 1;
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_DiffSource:
                multiplier = Math.SQRT2;
                break;
            case Consts.CONFIGURATION.DiffADC_SEAmp_SESource:
                multiplier = Math.sqrt(5);
                break;
            default:
                // Consts.CONFIGURATION.SEADC
                multiplier = 1;
        }

        return multiplier * noiseGain;
    }

    @computed('noiseGain') get ampIbPlusNoiseMultiplier() {
        return this.noiseGain;
    }

    @computed('ampIbMinusNoiseDensityInput', 'ampIbMinusNoiseMultiplier') get ampIbMinusNoiseDensity() {
        return this.ampIbMinusNoiseDensityInput * this.ampIbMinusNoiseMultiplier;
    }

    @computed('ampIbPlusNoiseDensityInput', 'ampIbPlusNoiseMultiplier') get ampIbPlusNoiseDensity() {
        return this.ampIbPlusNoiseDensityInput * this.ampIbPlusNoiseMultiplier;
    }

    @computed('adcNoiseCalculationModel.noiseDensity', 'sigmaDeltaData.{isSigmaDeltaAdc,adcNoiseDensity}')
    get adcNoiseDensity() {
        if (this.adcNoiseCalculationModel) return this.adcNoiseCalculationModel.noiseDensity;
        else if (this.sigmaDeltaData.isSigmaDeltaAdc) return this.sigmaDeltaData.adcNoiseDensity;
        else return '';
    }

    @computed(
        'adcNoiseCalculationModel.noiseVrms',
        'selectedAdc.adcNoiseRms',
        'sigmaDeltaData.{isSigmaDeltaAdc,adcNoiseDensity,adcEquivNoiseBW}',
    )
    get adcNoiseContribution() {
        if (this.adcNoiseCalculationModel) return this.adcNoiseCalculationModel.noiseVrms;
        if (this.sigmaDeltaData.isSigmaDeltaAdc)
            return Math.sqrt(this.sigmaDeltaData.adcEquivNoiseBW) * this.adcNoiseDensity;
        else return this.selectedAdc.adcNoiseRms;
    }

    //#endregion noise density calculations

    //#endregion noise

    //---------------------------------------------------
    //---------------------------------------------------

    //#region thd calculations
    @computed('thd', 'gain') get adjustedSourceThd() {
        let thd = this.thd,
            gain = this.gain;

        return -thd + 20 * Math.log10(gain);
    }

    // #region THD Adjustments
    @computed(
        'selectedAdc.{cadc,maxSampleRate,acquisitionTimeMin,settlingTHDadj,Vref_settlingTHDadj,THD_settlingTHDadj,tacq_settlingTHDadj,R_settlingTHDadj,C_settlingTHDadj,fs_settlingTHDadj}',
        'samplingRate',
        'cext',
        'rext',
    )
    get thdSettlingAdjustment() {
        let result = false;
        let adc = this.selectedAdc;
        let cext = ['AD4080', 'AD4081', 'AD4084'].includes(this.selectedAdc.partCore) ? 2 * this.cext : this.cext;
        let rext = this.rext;
        let samplingRate = this.samplingRate;

        if (adc.settlingTHDadj) {
            let thdSettlingThdAdjLinear =
                (0.7071 / 2) * adc.Vref_settlingTHDadj * (1 / Math.pow(10, adc.THD_settlingTHDadj / 20));
            let vpSettlingThdAdj =
                thdSettlingThdAdjLinear /
                Math.exp(-adc.tacq_settlingTHDadj / (adc.R_settlingTHDadj * adc.C_settlingTHDadj));
            let qSettlingThdAdj =
                vpSettlingThdAdj *
                adc.C_settlingTHDadj *
                (1 - Math.exp(-1 / (adc.fs_settlingTHDadj * adc.R_settlingTHDadj * adc.C_settlingTHDadj)));
            // let pcInfCSettlingThdAdj = qSettlingThdAdj * adc.fs_settlingTHDadj * adc.R_settlingTHDadj;

            console.log(this.selectedAdc.part, 'ADC Q model, Q value = ', qSettlingThdAdj);

            let tConv = 1 / adc.maxSampleRate - adc.acquisitionTimeMin;
            let tAcqActual = 1 / samplingRate - tConv;
            let vpActual =
                ((qSettlingThdAdj / (cext + adc.cadc)) * 1) /
                (1 - Math.exp(-1 / (samplingRate * rext * (cext + adc.cadc))));
            let distortionSettlingActual = vpActual * Math.exp(-tAcqActual / (rext * (cext + adc.cadc)));
            let thdSettlingAdjustment =
                -20 * Math.log10((adc.Vref_settlingTHDadj / distortionSettlingActual) * (0.7071 / 2));

            result = thdSettlingAdjustment;
        }

        return result;
    }
    // #endregion THD Adjustments

    @computed('selectedDriver.id', 'samplingRate', 'rg', 'rf', 'rext', 'cext', 'frequency', 'selectedTopology')
    get driverPerformanceData() {
        let data = {
            tempABError: false,
            tempABErrorItems: [],
        };

        // Is this still the right calculation for the freq array?
        let frequency = [],
            phase = [],
            magnitude = [],
            endFrequency = (this.samplingRate * 0.5) / this.averaging,
            startFrequency = Math.pow(10, (Math.log10(endFrequency) - +4).toFixed(0)),
            rext = this.rext,
            cext = ['AD4080', 'AD4081', 'AD4084'].includes(this.selectedAdc.partCore) ? 2 * this.cext : this.cext,
            rf = this.rf,
            rg = this.rg,
            gbw = this.selectedDriver.unityGain,
            rout = this.selectedDriver.Ro,
            complexATopTerm = Complex.createComplex(32 * Math.PI * Math.PI * Math.PI * gbw * gbw * gbw, 0);

        // Temporary check
        if (rg !== Infinity) {
            for (let i = 0; i < 250; i++) {
                // Build frequency
                if (i !== 0)
                    frequency[i] = Math.pow(
                        10,
                        Math.log10(frequency[i - 1]) + Math.log10(endFrequency / startFrequency) / 80,
                    );
                else frequency[i] = Math.pow(10, Math.round(Math.log10(startFrequency)));

                var sImaginary = 2 * Math.PI * frequency[i],
                    sSquared = -1 * sImaginary * sImaginary,
                    //complexABottomTerm = Complex.createComplex(sSquared, 4 * Math.PI * gbw * sImaginary),
                    complexABottomTerm = Complex.createComplex(
                        8 * gbw * Math.PI * sSquared,
                        sImaginary * (16 * gbw * gbw * Math.PI * Math.PI + sSquared),
                    ),
                    //complexA = (complexRparallel.divide(complexRout)).multDestroy(complexATopTerm.divide(complexABottomTerm)),
                    complexA = complexATopTerm.divide(complexABottomTerm),
                    complexBTopTerm = Complex.createComplex(rg, rg * rext * cext * sImaginary),
                    complexBBottomTerm = Complex.createComplex(
                        rout + rf + rg,
                        (rext * cext * (rf + rg + rout) + cext * rout * (rf + rg)) * sImaginary,
                    ),
                    complexB = complexBTopTerm.divide(complexBBottomTerm),
                    complexResult = complexA.mult(complexB);

                magnitude.push(complexResult.magnitude());
                //phase.push(atan2(complexResult.imaginary, complexResult.real) * (360 / (2 * Math.PI)));

                let ph = Math.atan2(complexResult.imaginary, complexResult.real) * (360 / (2 * Math.PI));
                if (ph < -80) phase.push(ph);
                else phase.push(-181);
            }

            data.abFrequencyArray = frequency;
            data.abMagnitudeArray = magnitude;
            data.abPhaseArray = phase;
        } else {
            let intl = this.intl;

            data.tempABError = intl.t('messages.rg_inf').toString();
            data.tempABErrorItems = [
                intl.t('messages.topology_to_inv').toString(),
                intl.t('messages.gain_to_not_one').toString(),
            ];
        }

        return data;
    }

    @computed('selectedAdc.id', 'frequency', 'rext')
    get adcThdEstimate() {
        let adc = this.selectedAdc,
            frequency = this.frequency,
            rext = this.rext;

        return Formulas.calculateThd(frequency, rext, adc);
    }

    @computed('selectedAdc.id', 'frequency')
    get adcThdDatasheet() {
        let adc = this.selectedAdc,
            datasheetRfilt = adc.datasheetRfilt,
            frequency = this.frequency;

        return Formulas.calculateThd(frequency, datasheetRfilt, adc);
    }

    @alias('circuitConfigurations.currentCircuitConfig.secondDriver') secondDriver;
    @alias('circuitConfigurations.currentCircuitConfig.config') noiseConfig;
    @alias('circuitConfigurations.currentCircuitConfig.nInvertingEnabled') nInvertingEnabled;
    @alias('circuitConfigurations.currentCircuitConfig.svg') circuitSvg;

    @computed('hasSequencerMode') get circuitInfoModals() {
        if (!this.hasSequencerMode) return [];
        else
            return [
                {
                    name: 'Seq',
                    bodyArgs: {},
                },
            ];
    }

    @computed('samplingRate') get samplePeriod() {
        let samplingRate = this.samplingRate;
        return 1 / samplingRate;
    }

    @computed('vref', 'selectedAdc.{type,bits}') get oneLsb() {
        let result;

        let vref = this.vref,
            adcType = this.selectedAdc.type,
            bits = this.selectedAdc.bits;

        if (adcType === Consts.ADC_TYPE.DIFF) {
            result = (2 * vref) / Math.pow(2, bits);
        } else {
            result = vref / Math.pow(2, bits);
        }

        return result;
    }

    //# endregion thd calculations

    @computed('vout', 'vref', 'samplePeriod', 'selectedAdc.bits') get requiredBwMuxedTransition() {
        let vout = this.vout,
            vref = this.vref,
            samplePeriod = this.samplePeriod,
            bits = this.selectedAdc.bits;

        return Formulas.calculateRequiredBw(vout, vref, bits, samplePeriod);
    }

    @computed(
        'selectedDriver.id',
        'selectedAdc.id',
        'samplePeriod',
        'frequency',
        'cext',
        'rext',
        'vref',
        'vout',
        'vcm',
        'vp',
        'rg',
        'rf',
        'cf',
        'vSupplyPos',
        'vSupplyNeg',
        'selectedTopology.id',
        'gain',
        'inputSlewRate',
    )
    get inputSettlingArrays() {
        return this.calculateInputSettlingArrays();
    }

    calculateInputSettlingArrays() {
        // If the sampling rate values are out of bounds, skip the computation - this saves a TON of processing for plots
        // that will not appear anyway.
        if (this.samplingRate > this.selectedAdc.maxSampleRate || this.samplingRate < this.minSamplingRate) {
            return {};
        }

        // Inputs
        let rext = this.rext;
        let cext = ['AD4080', 'AD4081', 'AD4084'].includes(this.selectedAdc.partCore) ? 2 * this.cext : this.cext;
        let vref = this.vref;
        let gain = this.gain;
        let rf = this.rf;
        let rg = this.rg;
        let cf = this.cf;
        let topologyId = this.selectedTopology.id;
        let selectedAdc = this.selectedAdc;
        let selectedDriver = this.selectedDriver;
        let vSupplyPos = this.vSupplyPos;
        let vSupplyNeg = this.vSupplyNeg;

        let timeStep = 1e-10; // TODO: This should come from input from "Simulation Settings"
        let period = this.samplePeriod;
        let numSteps = Math.round(period / timeStep);
        let numSteps2 = numSteps * 2;
        let numSteps3 = numSteps * 3;
        // This removes 80% of the last cycle.
        // let numSteps3 = numSteps * 2 + (0.2 * numSteps);
        // conductances of cf and ci
        const hasCf = cf && cf > 0;
        let gcf = (2 * cf) / timeStep;
        let gci = hasCf ? (2 * (selectedDriver.Cin + selectedDriver.Cdiff)) / timeStep : 0;
        let gcext = (2 * cext) / timeStep;
        let alpha = selectedDriver.unityGain * Math.PI * timeStep;
        let maxVpoleChange = selectedDriver.slewRate * timeStep;
        let bias = this.vcm;
        let vp = this.vp;
        let inputSlewRate = topologyId === Consts.TOPOLOGY.INVERTING ? -1 * this.inputSlewRate : this.inputSlewRate;
        // For muxed simulations.
        let voutMin = bias - vp;
        let voutMax = bias + vp;
        let vinMin = Formulas.vinFromVout(voutMin, bias, rg, rf, gain, topologyId);
        let vinMax = Formulas.vinFromVout(voutMax, bias, rg, rf, gain, topologyId);
        // For dc simulations.
        let voutDc = voutMax;
        let vinDc = Formulas.vinFromVout(voutDc, bias, rg, rf, gain, topologyId);
        // Vin arrays
        let vinMuxedA = [];
        let vinDcA = [];
        let vinSinA = [];

        let vcapStepMuxed = 0;
        let vcapStepDc = 0;
        let vcapStepSin = 0;

        // #region Construct Vin arrays
        for (let i = 0; i < 5; i++) {
            vinMuxedA.push(selectedAdc.internalMux ? vinMax : vinMin);
            vinDcA.push(vinDc);
            vinSinA.push(bias);
        }

        // These loops are looping far more times than necessary. Look at old arrays to see how/if this number was reduced...
        for (let i = 5; i < numSteps; i++) {
            vinMuxedA.push(selectedAdc.internalMux ? vinMax : vinMin);
            vinDcA.push(vinDc);
            vinSinA.push(timeStep * inputSlewRate + vinSinA[i - 1]);
        }

        for (let i = numSteps; i <= numSteps3; i++) {
            vinMuxedA.push(vinMax);
            vinDcA.push(vinDc);
            vinSinA.push(timeStep * inputSlewRate + vinSinA[i - 1]);
        }
        // #endregion Construct Vin arrays

        // #region Start construction of lsbError, vinIdeal, Vpole, Vout, Vcap, Icap, and Icapeq arrays
        // Vout vars
        let voutMuxedA = selectedAdc.internalMux ? voutMax : voutMin;
        let voutDcA = voutDc;
        let voutSinA = bias;

        // Vcap arrays
        let vcapMuxedA = [voutMuxedA];
        let vcapDcA = [voutDcA];
        let vcapSinA = [bias - Math.abs(inputSlewRate) * rext * cext];

        //Vinminus vars
        let vinMinusMuxed = Formulas.calculateVinMinus(voutMuxedA, vinMuxedA[0], bias, rg, rf, gain, topologyId);
        let vinMinusDc = Formulas.calculateVinMinus(voutDcA, vinDcA[0], bias, rg, rf, gain, topologyId);
        let vinMinusSin = Formulas.calculateVinMinus(voutSinA, vinSinA[0], bias, rg, rf, gain, topologyId);

        // Vpole vars
        let vpoleMuxedA = Formulas.calculateVpoleStart(
            voutMuxedA,
            vcapMuxedA[0],
            vinMinusMuxed,
            rext,
            rf,
            selectedDriver.Ro,
        );
        let vpoleDcA = Formulas.calculateVpoleStart(voutDc, vcapDcA[0], vinMinusDc, rext, rf, selectedDriver.Ro);
        let vpoleSinA = Formulas.calculateVpoleStart(bias, vcapSinA[0], vinMinusSin, rext, rf, selectedDriver.Ro);

        // Vin Ideal arrays
        let vinIdealMuxedA = [];
        let vinIdealDcA = [];
        let vinIdealSinA = [];

        // LSB Error arrays
        let lsbErrorMuxedA = [];
        let lsbErrorDcA = [];
        let lsbErrorSinA = [];

        // Volts Error arrays
        let voltsErrorMuxedA = [];
        let voltsErrorDcA = [];
        let voltsErrorSinA = [];

        // 1. assume steady state voltages and capacitor currents at t=0
        // 2. use 1 to determine companion current sources.
        // 3. use companion current sources to determine new voltages at t + dt
        // 4. use new voltages to determine new capacitor currents at t + dt
        // 5. repeat from step 2 with t = t + dt

        // pseudo code for steps 1 to 5:
        // initialize voltages and currents at t=0
        // for i = 0 to numSteps
        //    calculate companion current sources at t + dt using voltages and currents at t
        //    calculate voltages at t + dt using companion current sources at t + dt
        //    calculate currents at t + dt using voltages at t + dt
        // end for

        let vinPlusMuxed = Formulas.calculateVinMinusPlus(vinMuxedA[0], bias, gain, topologyId);
        let vinPlusDc = Formulas.calculateVinMinusPlus(vinDcA[0], bias, gain, topologyId);
        let vinPlusSin = Formulas.calculateVinMinusPlus(vinSinA[0], bias, gain, topologyId);

        let ieqciMuxedA = hasCf ? -gci * vinMinusMuxed : 0;
        let ieqciDcA = hasCf ? -gci * vinMinusDc : 0;
        let ieqciSinA = hasCf ? -gci * vinMinusSin : 0;

        let ieqcfMuxedA = hasCf ? -gcf * (voutMuxedA - vinMinusMuxed) : 0;
        let ieqcfDcA = hasCf ? -gcf * (voutDcA - vinMinusDc) : 0;
        let ieqcfSinA = hasCf ? -gcf * (voutSinA - vinMinusSin) : 0;

        let ieqcextMuxedA = -gcext * vcapMuxedA[0];
        let ieqcextDcA = -gcext * vcapDcA[0];
        let ieqcextSinA = -gcext * vcapSinA[0];

        let veqMuxedA = vpoleMuxedA + alpha * (vinPlusMuxed - vinMinusMuxed);
        let veqDcA = vpoleDcA + alpha * (vinPlusDc - vinMinusDc);
        let veqSinA = vpoleSinA + alpha * (vinPlusSin - vinMinusSin);

        // #endregion Start construction of lsbError, vinIdeal, Vpole, Vout, VinPlus, Vcap, Icap, and Icapeq arrays

        // #region Construct time array and kickback
        let tConvMax = 1 / selectedAdc.maxSampleRate - selectedAdc.acquisitionTimeMin;

        // Time array
        let timeA = [];
        let timeAtKickback = 0;

        let minKickbackDominatesSin = false;
        // let minKickbackDominatesMuxed = false;
        // let minKickbackDominatesDc = false;

        let inputFrequencyWhereSlewDominatesKickback = null;
        let slewDominatesKickback = false;

        let requiredBwMuxed;
        let requiredBwDc;
        let requiredBwSin;

        let kickbackFound = false;
        let kickbackIndex = 0;
        let nonDecimateArray = []; // This is for keeping the kickback during decimation.

        // for headroom/clamping of vpole node - most needed for multiplexed simulation with larger Ro, smaller Rfilt
        let clampHigh = vSupplyPos - selectedDriver.VoutHdrmPos;
        let clampLow = vSupplyNeg + selectedDriver.VoutHdrmNeg;

        const params = {
            // These params are constant for each simulation, so we can calculate them once and pass them to the
            bias,
            rg,
            rf,
            ro: selectedDriver.Ro,
            rext,
            gain,
            alpha,
            topology: topologyId,
            gci,
            gcf,
            gcext,
        };

        for (let i = 0; i <= numSteps3; i++) {
            let time = timeStep * i - period; /* * 1e9 converts to ns*/ //* 1e9;

            timeA.push(time);

            vinIdealMuxedA.push(Formulas.calculateIdealVin(vinMuxedA[i], bias, gain, topologyId));
            vinIdealDcA.push(Formulas.calculateIdealVin(vinDcA[i], bias, gain, topologyId));
            vinIdealSinA.push(Formulas.calculateIdealVin(vinSinA[i], bias, gain, topologyId));

            let {
                vm: vinMinusMuxed,
                vout: voutMuxedA,
                vpole: newVpoleMuxedA,
                vp: vinPlusMuxed,
                vcap: vcapMuxed,
            } = Formulas.calculateVoltages({
                ...params,
                vin: vinMuxedA[i],
                ieqci: ieqciMuxedA,
                ieqcf: ieqcfMuxedA,
                ieqcext: ieqcextMuxedA,
                veq: veqMuxedA,
            });
            let {
                vm: vinMinusDc,
                vout: voutDcA,
                vpole: newVpoleDcA,
                vp: vinPlusDc,
                vcap: vcapDc,
            } = Formulas.calculateVoltages({
                ...params,
                vin: vinDcA[i],
                ieqci: ieqciDcA,
                ieqcf: ieqcfDcA,
                ieqcext: ieqcextDcA,
                veq: veqDcA,
            });
            let {
                vm: vinMinusSin,
                vout: voutSinA,
                vpole: newVpoleSinA,
                vp: vinPlusSin,
                vcap: vcapSin,
            } = Formulas.calculateVoltages({
                ...params,
                vin: vinSinA[i],
                ieqci: ieqciSinA,
                ieqcf: ieqcfSinA,
                ieqcext: ieqcextSinA,
                veq: veqSinA,
            });

            vpoleMuxedA = Math.max(
                vpoleMuxedA - maxVpoleChange,
                Math.min(vpoleMuxedA + maxVpoleChange, newVpoleMuxedA),
            );
            vpoleDcA = Math.max(vpoleDcA - maxVpoleChange, Math.min(vpoleDcA + maxVpoleChange, newVpoleDcA));
            vpoleSinA = Math.max(vpoleSinA - maxVpoleChange, Math.min(vpoleSinA + maxVpoleChange, newVpoleSinA));

            // clamp vpole
            vpoleMuxedA = Math.max(Math.min(vpoleMuxedA, clampHigh), clampLow);
            vpoleDcA = Math.max(Math.min(vpoleDcA, clampHigh), clampLow);
            // vpoleSinA = Math.max(Math.min(vpoleSinA, clampHigh), clampLow);

            let currentVcapMuxed = vcapMuxed;
            let currentVcapDc = vcapDc;
            let currentVcapSin = vcapSin;

            // Vcap with kickback
            if (!kickbackFound && time > tConvMax) {
                kickbackFound = true;
                kickbackIndex = i + 1;

                timeAtKickback = time;

                // Set items to save from decimation around the kickback.
                nonDecimateArray.push(i - 1, i, i + 1);

                // Calculating kickback and recoding if min kickback dominates.
                let kickbackObj = Formulas.calculateKickback(
                    vcapMuxedA[i],
                    selectedAdc.internalMux && !selectedAdc.hasCurrentVariant ? 0 : vcapMuxedA[numSteps],
                    vref,
                    selectedAdc.kickbackType,
                    selectedAdc.kickbackVrefMultiplier,
                    cext,
                    selectedAdc.cadc,
                );

                currentVcapMuxed = kickbackObj.kickback;
                // minKickbackDominatesMuxed = kickbackObj.minKickbackDominates;
                vcapStepMuxed = kickbackObj.kickbackMag;

                kickbackObj = Formulas.calculateKickback(
                    vcapDcA[i],
                    vcapDcA[numSteps],
                    vref,
                    selectedAdc.kickbackType,
                    selectedAdc.kickbackVrefMultiplier,
                    cext,
                    selectedAdc.cadc,
                );
                currentVcapDc = kickbackObj.kickback;
                // minKickbackDominatesDc = kickbackObj.minKickbackDominates;
                vcapStepDc = kickbackObj.kickbackMag;

                kickbackObj = Formulas.calculateKickback(
                    vcapSinA[i],
                    vcapSinA[numSteps],
                    vref,
                    selectedAdc.kickbackType,
                    selectedAdc.kickbackVrefMultiplier,
                    cext,
                    selectedAdc.cadc,
                );

                currentVcapSin = kickbackObj.kickback;
                minKickbackDominatesSin = kickbackObj.minKickbackDominates;
                vcapStepSin = kickbackObj.kickbackMag;

                inputFrequencyWhereSlewDominatesKickback =
                    this.selectedAdc.kickbackVrefMultiplier /
                    ((selectedAdc.continuousAcquisition ? period : tConvMax) * Math.PI);
                slewDominatesKickback = PrettyNum.lessThan(
                    PrettyNum.eng3(this.frequency),
                    PrettyNum.eng3(inputFrequencyWhereSlewDominatesKickback),
                );

                if (this.frequency > inputFrequencyWhereSlewDominatesKickback && selectedAdc.continuousAcquisition) {
                    //Once input frequency exceeds slew dominated kick, update AC kickback step only in continuous acquisition mode
                    vcapStepSin = (Math.abs(inputSlewRate) * selectedAdc.cadc * period) / (cext + selectedAdc.cadc);
                }

                gcext = (2 * (cext + selectedAdc.cadc)) / timeStep;
                params.gcext = gcext;
                ieqcextMuxedA = -gcext * currentVcapMuxed;
                ieqcextDcA = -gcext * currentVcapDc;
                ieqcextSinA = -gcext * currentVcapSin;
            }
            // remove the early glitch from mux break time - simlifies the results for the customer
            // else if (!tZeroFound && time > muxBreakTime && selectedAdc.internalMux) {  // mux swtich kickback for internal mux devices
            //     tZeroFound = true;
            //     let kickbackObjMuxed = Formulas.calculateKickback(vcapMuxedA[i], 0, vref, selectedAdc.kickbackType, selectedAdc.kickbackVrefMultiplier, cext, selectedAdc.cpin);
            //     let kickbackObjDC = Formulas.calculateKickback(vcapDcA[i], 0, vref, selectedAdc.kickbackType, selectedAdc.kickbackVrefMultiplier, cext, selectedAdc.cpin);
            //     let kickbackObjSin = Formulas.calculateKickback(vcapSinA[i], 0, vref, selectedAdc.kickbackType, selectedAdc.kickbackVrefMultiplier, cext, selectedAdc.cpin);
            //     vcapMuxedA.push(kickbackObjMuxed.kickback);
            //     vcapDcA.push(kickbackObjDC.kickback);
            //     vcapSinA.push(kickbackObjSin.kickback);
            // }
            vcapMuxedA.push(currentVcapMuxed);
            vcapDcA.push(currentVcapDc);
            vcapSinA.push(currentVcapSin);

            if (kickbackFound && kickbackIndex === i + 1) {
                requiredBwMuxed = Formulas.calculateRequiredBw(
                    vcapMuxedA[i + 1] - vcapMuxedA[i],
                    vref,
                    selectedAdc.bits,
                    period - tConvMax,
                );
                requiredBwDc = Formulas.calculateRequiredBw(
                    vcapDcA[i + 1] - vcapDcA[i],
                    vref,
                    selectedAdc.bits,
                    period - tConvMax,
                );
                requiredBwSin = Formulas.calculateRequiredBw(
                    vcapSinA[i + 1] - vcapSinA[i],
                    vref,
                    selectedAdc.bits,
                    period - tConvMax,
                );
            }

            // LSB Error Muxed and DC
            let currentVoltsErrorMuxedA = Formulas.calculateVoltsError(vcapMuxedA[i + 1], voutMax);
            //let currentLsbErrorMuxedA = Formulas.calculateLsbError(vcapMuxedA[i + 1], vref, selectedAdc.bits, voutMax);
            // let currentLsbErrorDcA = Formulas.calculateLsbError(vcapDcA[i + 1], vref, selectedAdc.bits, voutDc);
            let currentVoltsErrorDcA = Formulas.calculateVoltsError(vcapDcA[i + 1], voutDc);
            //let currentVoltsErrorSinA = Formulas.calculateVoltsError(vcapSinA[i + 1], voutSin)

            let currentLsbErrorMuxedA = Formulas.convertVoltsToLsb(currentVoltsErrorMuxedA, vref, selectedAdc.bits);
            let currentLsbErrorDcA = Formulas.convertVoltsToLsb(currentVoltsErrorDcA, vref, selectedAdc.bits);

            lsbErrorMuxedA.push(currentLsbErrorMuxedA);
            lsbErrorDcA.push(currentLsbErrorDcA);

            voltsErrorMuxedA.push(currentVoltsErrorMuxedA);
            voltsErrorDcA.push(currentVoltsErrorDcA);

            //for model troubleshooting
            //create debug string to write out to file
            /*
            timeString = timeString + ',' + String(time);
            capMuxedString = capMuxedString + ',' + String(vcapMuxedA[i]);
            outMuxedString = outMuxedString + ',' + String(voutMuxedA);
            poleMuxedString = poleMuxedString + ',' + String(vpoleMuxedA);
            capDCString = capDCString + ',' + String(vcapDcA[i]);
            outDCString = outDCString + ',' + String(voutDcA);
            poleDCString = poleDCString + ',' + String(vpoleDcA);
            capACString = capACString + ',' + String(vcapSinA[i]);
            outACString = outACString + ',' + String(voutSinA);
            poleACString = poleACString + ',' + String(vpoleSinA);
            */

            let iCiMuxedA = gci * vinMinusMuxed + ieqciMuxedA;
            let iCiDcA = gci * vinMinusDc + ieqciDcA;
            let iCiSinA = gci * vinMinusSin + ieqciSinA;

            let iCfMuxedA = gcf * (voutMuxedA - vinMinusMuxed) + ieqcfMuxedA;
            let iCfDcA = gcf * (voutDcA - vinMinusDc) + ieqcfDcA;
            let iCfSinA = gcf * (voutSinA - vinMinusSin) + ieqcfSinA;

            let iCextMuxedA = gcext * vcapMuxedA[i + 1] + ieqcextMuxedA;
            let iCextDcA = gcext * vcapDcA[i + 1] + ieqcextDcA;
            let iCextSinA = gcext * vcapSinA[i + 1] + ieqcextSinA;

            ieqciMuxedA = hasCf ? -gci * vinMinusMuxed - iCiMuxedA : 0;
            ieqciDcA = hasCf ? -gci * vinMinusDc - iCiDcA : 0;
            ieqciSinA = hasCf ? -gci * vinMinusSin - iCiSinA : 0;

            ieqcfMuxedA = hasCf ? -gcf * (voutMuxedA - vinMinusMuxed) - iCfMuxedA : 0;
            ieqcfDcA = hasCf ? -gcf * (voutDcA - vinMinusDc) - iCfDcA : 0;
            ieqcfSinA = hasCf ? -gcf * (voutSinA - vinMinusSin) - iCfSinA : 0;

            ieqcextMuxedA = -gcext * vcapMuxedA[i + 1] - iCextMuxedA;
            ieqcextDcA = -gcext * vcapDcA[i + 1] - iCextDcA;
            ieqcextSinA = -gcext * vcapSinA[i + 1] - iCextSinA;

            veqMuxedA = vpoleMuxedA + alpha * (vinPlusMuxed - vinMinusMuxed);
            veqDcA = vpoleDcA + alpha * (vinPlusDc - vinMinusDc);
            veqSinA = vpoleSinA + alpha * (vinPlusSin - vinMinusSin);
        }

        // for model troublehshooting
        /*
        let pythonString = "import matplotlib.pyplot as plt\r\nimport numpy as np\r\n";
        pythonString = pythonString + "plt.subplot(3, 1, 1)\r\n";
        //pythonString = pythonString + "plt1 = plt.figure()\r\n";
        pythonString = pythonString + "plt.title('Muxed " + selectedAdc.part + " + " + selectedDriver.part + " R = " + rext + " C = " + cext + "')\r\n";
        pythonString = pythonString + "plt.legend(['Pole output','Amp output','RC output'])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + poleMuxedString + "])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + outMuxedString + "])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + capMuxedString + "])\r\n";
        pythonString = pythonString + "plt.axhline(" + clampHigh +", color='grey', lw=1)\r\n";
        //pythonString = pythonString + "plt.show()\r\n";

        //pythonString = pythonString + "plt2 = plt.figure()\r\n";
        pythonString = pythonString + "plt.subplot(3, 1, 2)\r\n";
        pythonString = pythonString + "plt.title('DC " + selectedAdc.part + " + " + selectedDriver.part + " R = " + rext + " C = " + cext + "')\r\n";
        pythonString = pythonString + "plt.legend(['Pole output','Amp output','RC output'])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + poleDCString + "])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + outDCString + "])\r\n";
        pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + capDCString + "])\r\n";
        //pythonString = pythonString + "plt.axhline(" + clampHigh +", color='grey', lw=1)\r\n";
        //pythonString = pythonString + "plt.show()\r\n";

         //pythonString = pythonString + "plt3 = plt.figure()\r\n";
         pythonString = pythonString + "plt.subplot(3, 1, 3)\r\n";
         pythonString = pythonString + "plt.title('AC " + selectedAdc.part + " + " + selectedDriver.part + " R = " + rext + " C = " + cext + "')\r\n";
         pythonString = pythonString + "plt.legend(['Pole output','Amp output','RC output'])\r\n";
         pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + poleACString + "])\r\n";
         pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + outACString + "])\r\n";
         pythonString = pythonString + "plt.plot([0" + timeString + "],[np.nan" + capACString + "])\r\n";
         //pythonString = pythonString + "plt.axhline(" + clampHigh +", color='grey', lw=1)\r\n";
         pythonString = pythonString + "plt.show()\r\n";

        // troubleshooting model - comment out before publishing
        // debugger
        //console.save(pythonString, "MuxedArrays.py");
        */

        // Adjusts the Sin/AC ideal vin array by the differece of the ideal and actual at t===0/start of cycle 2,
        // so the ideal will line up with the actual.
        let diffSin = vcapSinA[numSteps] - vinIdealSinA[numSteps];
        let vinIdealAdjustedSinA = vinIdealSinA.map((x) => x + diffSin);

        // Volts Error AC
        voltsErrorSinA = vinIdealAdjustedSinA.map((x, i) => vcapSinA[i] - x);

        // LSB Error AC
        //lsbErrorSinA = vinIdealAdjustedSinA.map((x, i) => ((vcapSinA[i] - x) * (Math.pow(2, selectedAdc.bits)) / vref));
        lsbErrorSinA = voltsErrorSinA.map((volts) => Formulas.convertVoltsToLsb(volts, vref, selectedAdc.bits));

        // TODO: Look into moving these checks to after the decimation.
        // Check for ringing on the AC and DC LSB arrays. (Rings if > 1 after kickback)
        let lsbRingDc = false;
        let lsbRingSin = false;
        // let lsbRingMuxed = false;

        for (let i = kickbackIndex; i <= numSteps3; i++) {
            if (lsbErrorDcA[i] > 1) {
                lsbRingDc = true;
                break;
            }
        }

        for (let i = kickbackIndex; i <= numSteps3; i++) {
            if (lsbErrorSinA[i] > 1) {
                lsbRingSin = true;
                break;
            }
        }

        // for (let i = kickbackIndex; i <= numSteps3; i++) {
        //     if (lsbErrorMuxedA[i] > 1) {
        //         lsbRingMuxed = true;
        //         break;
        //     }
        // }

        // Find the absolute max LSB Error in the 3rd cycle.
        let lsbMaxMuxed = Math.abs(lsbErrorMuxedA[numSteps2]);
        let lsbMaxDc = Math.abs(lsbErrorDcA[numSteps2]);
        let lsbMaxSin = Math.abs(lsbErrorSinA[numSteps2]);

        let voltsMaxMuxed = Math.abs(voltsErrorMuxedA[numSteps2]);
        let voltsMaxDc = Math.abs(voltsErrorDcA[numSteps2]);
        let voltsMaxSin = Math.abs(voltsErrorSinA[numSteps2]);

        let len = timeA.length;
        for (let i = numSteps2; i < len; i++) {
            // Check muxed max.
            let currentMax = Math.abs(lsbErrorMuxedA[i]);
            if (lsbMaxMuxed < currentMax) lsbMaxMuxed = currentMax;

            // Check dc max.
            currentMax = Math.abs(lsbErrorDcA[i]);
            if (lsbMaxDc < currentMax) lsbMaxDc = currentMax;

            // Check sin max.
            currentMax = Math.abs(lsbErrorSinA[i]);
            if (lsbMaxSin < currentMax) lsbMaxSin = currentMax;

            // Check muxed volts max
            currentMax = Math.abs(voltsErrorMuxedA[i]);
            if (voltsMaxMuxed < currentMax) voltsMaxMuxed = currentMax;

            // Check dc volts max
            currentMax = Math.abs(voltsErrorDcA[i]);
            if (voltsMaxDc < currentMax) voltsMaxDc = currentMax;

            // Check ac volts max
            currentMax = Math.abs(voltsErrorSinA[i]);
            if (voltsMaxSin < currentMax) voltsMaxSin = currentMax;
        }

        // X and Y points to mark the start and end of cycle 2 on the vcap lines.
        let cycle1End = timeA[numSteps];
        let cycle3Start = timeA[numSteps2];

        let cycleMuxedLine = [
            [cycle1End, vcapMuxedA[numSteps]],
            [cycle3Start, vcapMuxedA[numSteps2]],
        ];
        let cycleDcLine = [
            [cycle1End, vcapDcA[numSteps]],
            [cycle3Start, vcapDcA[numSteps2]],
        ];
        let cycleSinLine = [
            [cycle1End, vcapSinA[numSteps]],
            [cycle3Start, vcapSinA[numSteps2]],
        ];

        let cycleMuxedLsbLine = [
            [cycle1End, lsbErrorMuxedA[numSteps]],
            [cycle3Start, lsbErrorMuxedA[numSteps2]],
        ];
        let cycleDcLsbLine = [
            [cycle1End, lsbErrorDcA[numSteps]],
            [cycle3Start, lsbErrorDcA[numSteps2]],
        ];
        let cycleSinLsbLine = [
            [cycle1End, lsbErrorSinA[numSteps]],
            [cycle3Start, lsbErrorSinA[numSteps2]],
        ];

        let lsb2ppmFactor = 1e6 / Math.pow(2, this.selectedAdc.bits);
        let lsb2ppmMapper = (err) => (typeof err === 'boolean' ? err : err * lsb2ppmFactor);
        let ppmErrorMuxedA = lsbErrorMuxedA.map(lsb2ppmMapper);
        let ppmErrorDcA = lsbErrorDcA.map(lsb2ppmMapper);
        let ppmErrorSinA = lsbErrorSinA.map(lsb2ppmMapper);

        let cycleMuxedPpmLine = [
            [cycle1End, ppmErrorMuxedA[numSteps]],
            [cycle3Start, ppmErrorMuxedA[numSteps2]],
        ];
        let cycleDcPpmLine = [
            [cycle1End, ppmErrorDcA[numSteps]],
            [cycle3Start, ppmErrorDcA[numSteps2]],
        ];
        let cycleSinPpmLine = [
            [cycle1End, ppmErrorSinA[numSteps]],
            [cycle3Start, ppmErrorSinA[numSteps2]],
        ];

        // let zzz = performance.now();
        // window.console.log('loop: ' + this.prettyNum.eng3((zzz - xxx) / 1000) + 's');

        // pop one off since there is one extra.
        vcapMuxedA.pop();
        vcapDcA.pop();
        vcapSinA.pop();
        // #endregion Construct time array and kickback

        // #region Decimation and setting
        // Subtract numToRemove to each item in the nonDecimateArray array.
        let numToRemove = Math.floor(numSteps * 0.8);
        // This is to keep the correct indexes after the first 80% of the first cycle is removed.
        nonDecimateArray = nonDecimateArray.map((x) => x - numToRemove);

        // Remove first 80% of the first cycle.
        vinIdealMuxedA.splice(0, numToRemove);
        vinIdealDcA.splice(0, numToRemove);
        vinIdealAdjustedSinA.splice(0, numToRemove);

        vcapMuxedA.splice(0, numToRemove);
        vcapDcA.splice(0, numToRemove);
        vcapSinA.splice(0, numToRemove);

        lsbErrorMuxedA.splice(0, numToRemove);
        lsbErrorDcA.splice(0, numToRemove);
        lsbErrorSinA.splice(0, numToRemove);

        timeA.splice(0, numToRemove);

        // Remove last 80% of the third/last cycle.
        let indexToRemove = timeA.length - numToRemove;
        vinIdealMuxedA.splice(indexToRemove);
        vinIdealDcA.splice(indexToRemove);
        vinIdealAdjustedSinA.splice(indexToRemove);

        vcapMuxedA.splice(indexToRemove);
        vcapDcA.splice(indexToRemove);
        vcapSinA.splice(indexToRemove);

        lsbErrorMuxedA.splice(indexToRemove);
        lsbErrorDcA.splice(indexToRemove);
        lsbErrorSinA.splice(indexToRemove);

        timeA.splice(indexToRemove);

        // Decimation.
        let decimatedArrays = Formulas.decimateArrays(
            [
                vinIdealMuxedA,
                vinIdealDcA,
                vinIdealAdjustedSinA,
                vcapMuxedA,
                vcapDcA,
                vcapSinA,
                lsbErrorMuxedA,
                lsbErrorDcA,
                lsbErrorSinA,
                timeA,
            ],
            1000,
            nonDecimateArray,
        );

        // TODO: Remove these logs after more testing.
        console.log(`settlingTHDadj:         ${!!selectedAdc.settlingTHDadj}`);
        console.log(`Dc rings:               ${lsbRingDc}`);
        console.log(`Sin rings:              ${lsbRingSin}`);
        console.log(`Dc settling error:      ${lsbMaxDc}`);
        console.log(`Sin settling error:     ${lsbMaxSin}`);
        console.log(`Dc settling error (V):  ${voltsMaxDc}`);
        console.log(`Sin settling error (V): ${voltsMaxSin}`);

        // Get LSB error values
        let lsbErrorMuxed = Formulas.removeConsecutiveValues(decimatedArrays[6], 1000000);
        let lsbErrorDc = Formulas.removeConsecutiveValues(decimatedArrays[7], 1000000);
        let lsbErrorSin = Formulas.removeConsecutiveValues(decimatedArrays[8], 1000000);

        // Calculate PPM values here based on LSB at the end instead of the beginning
        //let lsb2ppmFactor = 1e6 / (Math.pow(2, this.selectedAdc.bits));
        //let lsb2ppmMapper = (err) => typeof err === 'boolean' ? err : err * lsb2ppmFactor;
        let ppmErrorMuxed = lsbErrorMuxed.map(lsb2ppmMapper);
        let ppmErrorDc = lsbErrorDc.map(lsb2ppmMapper);
        let ppmErrorSin = lsbErrorSin.map(lsb2ppmMapper);

        let ppmMaxMuxed = lsbMaxMuxed * lsb2ppmFactor;
        let ppmMaxDc = lsbMaxDc * lsb2ppmFactor;
        let ppmMaxSin = lsbMaxSin * lsb2ppmFactor;

        //this.setProperties({
        let result = {
            vinDcInitial: vinDc, // for LTspice schematic
            vinCmInitial: bias,

            vinIdealMuxed: Formulas.removeConsecutiveValues(decimatedArrays[0], 100),
            vinIdealDc: Formulas.removeConsecutiveValues(decimatedArrays[1], 100),
            vinIdealAdjustedSin: Formulas.removeConsecutiveValues(decimatedArrays[2], 100),

            vcapMuxed: Formulas.removeConsecutiveValues(decimatedArrays[3], 1000000),
            vcapDc: Formulas.removeConsecutiveValues(decimatedArrays[4], 1000000),
            vcapSin: Formulas.removeConsecutiveValues(decimatedArrays[5], 1000000),

            // LSB Error
            lsbErrorMuxed,
            lsbErrorDc,
            lsbErrorSin,

            // PPM Error
            ppmErrorDc,
            ppmErrorMuxed,
            ppmErrorSin,

            time: decimatedArrays[9],

            settlingCycle1End: cycle1End,
            settlingCycle3Start: cycle3Start,
            timeAtKickback: timeAtKickback,

            voltsKickbackMuxed: vcapStepMuxed,
            voltsKickbackDc: vcapStepDc,
            voltsKickbackSin: vcapStepSin,

            lsbMaxMuxed,
            lsbMaxDc,
            lsbMaxSin,

            ppmMaxMuxed,
            ppmMaxDc,
            ppmMaxSin,

            voltsMaxMuxed,
            voltsMaxDc,
            voltsMaxSin,

            lsbRingDc,
            lsbRingSin,

            cycleMuxedLine,
            cycleDcLine,
            cycleSinLine,

            cycleMuxedLsbLine,
            cycleDcLsbLine,
            cycleSinLsbLine,

            cycleMuxedPpmLine,
            cycleDcPpmLine,
            cycleSinPpmLine,

            conversionTime: tConvMax,
            acquisitionTime: period - tConvMax,

            // minKickbackDominatesMuxed,
            // minKickbackDominatesDc,
            minKickbackDominatesSin,
            inputFrequencyWhereSlewDominatesKickback,
            slewDominatesKickback,

            requiredBwMuxed,
            requiredBwDc,
            requiredBwSin,
        };
        return result;
    }

    // #endregion Decimation and setting

    validateRules(rules, data) {
        let intl = this.intl;

        for (var i = 0; i < rules.length; i++) {
            let result = rules[i](data);

            if (result.inValid) {
                let bodyArgs = 'bodyArgs' in result && result.bodyArgs ? result.bodyArgs : {};

                return intl.t(result.translation, bodyArgs);
            }
        }
    }
}
